import { createApi } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import {envelopeDataTransformResponse} from "core";

import { axiosBaseQuery } from '../axiosBaseQuery';

import {
  CandidatePersonalDetails,
  ChildCandidates,
  ExportedRegistrationsCheck,
  GroupAwardsItem,
  RegistrationCandidate,
  RegistrationCandidateDetails,
} from './types';
import {
  CandidateProductDetailsRequest,
  ChildCandidatesResponse,
  ExportedRegistrationsCheckResponse,
  GroupAwardsItemResponse,
  PreviousCenterNumbersRequest,
} from './apiTypes';
import {
  mapCandidateResponse,
  mapGroupAwards,
  mapToCandidateDetailsRequest,
  mapToChildDetailsRequest,
} from './candidateHelpers';
import { has } from 'lodash-es';

export type CashinEligibilityCheckResponse = {
  messages: string[],
  showWarning: boolean
}

export const candidateApi = createApi({
  reducerPath: 'candidateApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api',
    paramsSerializer: (params: any) => queryString.stringify(params, {arrayFormat: 'none'}),
  }),
  endpoints: (builder) => ({
    getUsersCandidate: builder.query<RegistrationCandidate, void>({
      query: () => ({
        url: `/api/candidate/getLastUsersCandidate`,
        method: 'get',
      }),
      transformResponse: mapCandidateResponse,
    }),
    getCandidate: builder.query<RegistrationCandidate, number | null>({
      query: (id) => ({
        url: `/api/candidate/getLastUsersCandidate/${id}`,
        method: 'get',
      }),
      transformResponse: mapCandidateResponse,
    }),
    getChildCandidates: builder.query<ChildCandidates, void>({
      query: () => ({
        url: '/api/candidate/getChildCandidates',
        method: 'get',
      }),
      transformResponse: (res: ChildCandidatesResponse) => ({
        maxChildCandidates: res.MaxChildCandidates,
        children: res.ChildSummaries.map((x) => ({
          id: x.Id,
          familyName: x.FamilyName,
          otherNames: x.OtherNames,
          hasFamilyName: x.HasFamilyName,
          dateOfBirth: x.DOB,
          isMinorToAdultMigrationAvailable: x.IsMinorToAdultMigrationAvailable,
          isMinorToAdultMigrationInProgress: x.IsMinorToAdultMigrationInProgress,
        })),
      }),
    }),
    getEligibilityCheck: builder.query<CashinEligibilityCheckResponse, { sessionId: number, candidateId: number, examOptionIds: number[], districtId: number }>({
      query: (request) => ({
        url: '/b2c/api/candidate/eligibilityChecks',
        method: 'get',
        params: request
      }),
      transformResponse: envelopeDataTransformResponse
    }),
    validateCandidate: builder.mutation<void, RegistrationCandidateDetails>({
      query: (formModel) => ({
        url: '/api/examregistation/candidatevalidate',
        method: 'post',
        data: mapToCandidateDetailsRequest(formModel),
      }),
    }),
    parentDetailsValidate: builder.mutation<void, RegistrationCandidateDetails>({
      query: (formModel) => ({
        url: '/api/examregistation/parentDetailsValidate',
        method: 'post',
        data: mapToCandidateDetailsRequest(formModel),
      }),
    }),
    childDetailsValidate: builder.mutation<void, RegistrationCandidateDetails>({
      query: (formModel) => ({
        url: '/api/examregistation/childDetailsValidate',
        method: 'post',
        data: mapToChildDetailsRequest(formModel),
      }),
    }),
    exportedRegistrationsCheck: builder.query<ExportedRegistrationsCheck, number>({
      query: (candidateId) => ({
        url: '/api/examregistation/b2c/candidate/exportedRegistrationsCheck',
        method: 'post',
        data: { CandidateId: candidateId },
      }),
      transformResponse: (res: ExportedRegistrationsCheckResponse) => ({
        hasExportedExam: res.HasExportedExam,
        hasExportedNotCancelledExamsInCurrentAqaSession: res.HasExportedNotCancelledExamsInCurrentAqaSession,
        hasExportedNotCancelledExamsInCurrentEdexcelSession: res.HasExportedNotCancelledExamsInCurrentEdexcelSession,
        hasExportedRegistrationsInCurrentSessions: res.HasExportedRegistrationsInCurrentSessions,
        isCurrentAqaSession: res.IsCurrentAqaSession,
        isCurrentEdexcelSession: res.IsCurrentEdexcelSession,
      }),
    }),
    validateCentresNumbers: builder.mutation<void, PreviousCenterNumbersRequest>({
      query: (data) => ({
        url: '/api/examregistation/validateCentresNumbers',
        method: 'post',
        data: data,
      }),
    }),
    validateCandidateProductDetails: builder.mutation<void, CandidateProductDetailsRequest>({
      query: (data) => ({
        url: '/api/examregistation/candidateproductvalidate',
        method: 'post',
        data: data,
      }),
    }),
    getGroupAwardsData: builder.query<
      GroupAwardsItem[],
      {
        subSystem: string;
        countryId: number;
        districtId: number;
        sessionId: number;
        sessionStageId: number;
        candidateId?: number | null;
      }
    >({
      query: (data) => ({
        url: `/api/subsystems/${data.subSystem}/countries/${data.countryId}/districts/${data.districtId}/sessions/${
          data.sessionId
        }/stage/${data.sessionStageId}/candidates/${data.candidateId ?? 0}/groupAwards`,
        method: 'get',
      }),
      transformResponse: (data: { GroupAwards: GroupAwardsItemResponse[] }) => mapGroupAwards(data.GroupAwards),
    }),
    saveCandidateDetails: builder.mutation<void, CandidatePersonalDetails>({
      query: (data) => ({
        url: `/api/examregistation/b2c/candidateDetails/save`,
        method: 'post',
        data,
      }),
    }),
    startMinorToAdultMigration: builder.mutation<
      void,
      { candidateId: number; emailData: { minorEmail: string; confirmMinorEmail: string } }
    >({
      query: (data) => ({
        url: `/b2c/api/candidate/${data.candidateId}/startMinorToAdultMigration`,
        method: 'post',
        data: data.emailData,
      }),
    }),
    cancelMinorToAdultMigration: builder.mutation<void, { candidateId: number }>({
      query: (data) => ({
        url: `/b2c/api/candidate/${data.candidateId}/cancelMinorToAdultMigration`,
        method: 'post',
      }),
    }),
    childRequireMigration: builder.query<boolean, void>({
      query: () => ({
        url: `/b2c/api/candidate/childRequireMigration`,
        method: 'get',
      }),
      transformResponse: (response: any) => response.Data,
    }),
  }),
});

export const {
  useGetChildCandidatesQuery,
  useLazyGetCandidateQuery,
  useLazyGetUsersCandidateQuery,
  useParentDetailsValidateMutation,
  useValidateCandidateMutation,
  useChildDetailsValidateMutation,
  useLazyExportedRegistrationsCheckQuery,
  useExportedRegistrationsCheckQuery,
  useValidateCentresNumbersMutation,
  useValidateCandidateProductDetailsMutation,
  useGetEligibilityCheckQuery,
  useGetGroupAwardsDataQuery,
  useSaveCandidateDetailsMutation,
  useStartMinorToAdultMigrationMutation,
  useCancelMinorToAdultMigrationMutation,
  useLazyChildRequireMigrationQuery,
  endpoints,
} = candidateApi;
